import React, { useState } from "react";
import { Trash2, X } from "lucide-react";

const NeuroQuestionModal = ({
  isOpen,
  onClose = () => {}, 
  onSave,
}) => {
  const [questions, setQuestions] = useState([{
    question: '',
    options: ['', '', '', ''],
    images: [],
    marks: ''
  }]);

  const onSubmit = (e) => {
    e.preventDefault();
    console.log("Complete Data:", questions);
    if (onSave) {
      onSave(questions);
    }
    onClose();
  };

  const handleClose = () => {
    setQuestions([{
      question: '',
      options: ['', '', '', ''],
      images: [],
      marks: ''
    }]);
    if (onClose) {
      onClose();
    }
  };

  const handleAddQuestion = () => {
    if (questions.length >= 3) return; // Limit to 3 questions
    setQuestions([...questions, {
      question: '',
      options: ['', '', '', ''],
      images: [],
      marks: ''
    }]);
  };

  const handleDeleteQuestion = (indexToDelete) => {
    setQuestions(questions.filter((_, index) => index !== indexToDelete));
  };

  const handleQuestionChange = (index, value) => {
    const newQuestions = [...questions];
    newQuestions[index].question = value;
    setQuestions(newQuestions);
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    const newQuestions = [...questions];
    newQuestions[questionIndex].options[optionIndex] = value;
    setQuestions(newQuestions);
  };

  const handleMarksChange = (index, value) => {
    const newQuestions = [...questions];
    newQuestions[index].marks = value;
    setQuestions(newQuestions);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newQuestions = [...questions];
    
    // Clear existing images for the current question
    newQuestions[0].images = [];
    
    // Limit images to the number of questions
    const maxImages = questions.length;
    const filesToProcess = files.slice(0, maxImages);
    
    filesToProcess.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newQuestions[0].images.push(reader.result);
        setQuestions([...newQuestions]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (imageIndex) => {
    const newQuestions = [...questions];
    newQuestions[0].images.splice(imageIndex, 1);
    setQuestions(newQuestions);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white w-3/4 max-h-[80vh] overflow-y-auto rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold">
            Add Questions
          </h2>
          <button
            type="button"
            onClick={handleAddQuestion}
            disabled={questions.length >= 3}
            className={`bg-blue-500 text-white px-4 py-2 rounded ${
              questions.length >= 3 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
            }`}
          >
            Add Question
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          {questions.map((questionObj, qIndex) => (
            <div key={qIndex} className="relative border border-gray-200 p-4 rounded">
              <div className="flex justify-between items-start mb-2">
                <label className="block font-medium">
                  Question {qIndex + 1}
                </label>
                {questions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleDeleteQuestion(qIndex)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
              <div>
                <textarea
                  value={questionObj.question}
                  onChange={(e) => handleQuestionChange(qIndex, e.target.value)}
                  className="w-full border border-gray-300 p-2 rounded"
                  placeholder="Enter your question"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-3">
                {questionObj.options.map((option, oIndex) => (
                  <div key={oIndex}>
                    <input
                      value={option}
                      onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}
                      className="w-full border border-gray-300 p-2 rounded"
                      placeholder={`Enter option ${oIndex + 1}`}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-3">
                <label className="block font-medium mb-1">Marks</label>
                <input
                  type="number"
                  value={questionObj.marks}
                  onChange={(e) => handleMarksChange(qIndex, e.target.value)}
                  className="w-full border border-gray-300 p-2 rounded"
                  placeholder="Enter marks for this question"
                  min="0"
                />
              </div>
            </div>
          ))}

          <div className="mt-4 border border-gray-200 p-4 rounded">
            <h3 className="text-lg font-medium mb-2">Add Images (Max {questions.length} images)</h3>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="mb-2"
            />
            <div className="grid grid-cols-4 gap-2">
              {questions[0].images.map((image, imgIndex) => (
                <div key={imgIndex} className="relative">
                  <img 
                    src={image} 
                    alt={`Image ${imgIndex + 1}`}
                    className="w-full h-24 object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(imgIndex)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
  
          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-400 text-white px-6 py-2 rounded hover:bg-gray-500"
            >
              Close
            </button>
            <button
              type="submit"
              className="bg-[#241763] text-white px-6 py-2 rounded hover:bg-[#241763]/80"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NeuroQuestionModal;